import * as React from 'react';
import { XIcon, ArrowRightIcon } from '@heroicons/react/outline';
import { FiZap } from 'react-icons/fi';
import { useMemberOffers } from 'context/MemberOffersContext';
import { trackEvent } from 'services/client/analytics';
import { MEMBER_SALE_PRESENTED } from 'services/client/analytics/events';
import Countdown from 'components/MemberOffers/Countdown';
import { getMemberSaleStrip } from 'components/MemberOffers/memberOfferCopy';
import { SaleTerm } from 'constants/payments';

/**
 * The member sale strip above the trading screen, in the PR 308 banner's anatomy: the sale's name,
 * its number in one line, a real countdown and one action. It stays until the member closes it or
 * turns the sale down in the offer; opening the offer hides it for the visit. Its button opens the
 * plan the sale is on: a Lifetime sale opens Lifetime.
 */
export default function SaleBanner() {
  const offers = useMemberOffers();
  const campaign = offers.campaign;
  const row = offers.subscriptions.find(
    (subscription) =>
      subscription.sale?.id === campaign?.id || subscription.lifetime?.sale?.id === campaign?.id,
  );
  const strip = row ? getMemberSaleStrip(row) : null;
  const term =
    row && row.sale?.id !== campaign?.id && row.lifetime?.sale?.id === campaign?.id
      ? SaleTerm.Lifetime
      : SaleTerm.Annual;
  const visible = !!campaign && offers.showBanner && !offers.isOpen;
  const recorded = React.useRef<string>();
  React.useEffect(() => {
    if (!visible || !campaign || recorded.current === campaign.id) return;
    // An impression counts when a member can see it, not when a background tab renders it.
    const record = () => {
      if (document.visibilityState !== 'visible' || recorded.current === campaign.id) return;
      recorded.current = campaign.id;
      trackEvent(MEMBER_SALE_PRESENTED, { campaign: campaign.id });
    };
    record();
    document.addEventListener('visibilitychange', record);
    return () => document.removeEventListener('visibilitychange', record);
  }, [visible, campaign]);
  if (!visible || !campaign || !strip) return null;
  return (
    <SaleBannerView
      strip={strip}
      endsAt={campaign.endsAt}
      clockOffset={offers.clockOffset}
      onOpen={() => offers.open(row?.id, undefined, 'banner', term)}
      onDismiss={offers.dismissBanner}
    />
  );
}

interface BannerProps {
  strip: NonNullable<ReturnType<typeof getMemberSaleStrip>>;
  endsAt: number;
  clockOffset: number;
  onOpen: () => void;
  onDismiss: () => void;
  isPreview?: boolean;
}

export function SaleBannerView({
  strip,
  endsAt,
  clockOffset,
  onOpen,
  onDismiss,
  isPreview = false,
}: BannerProps) {
  return (
    <div
      className="relative overflow-hidden border-b border-emerald-300/35 bg-[#0b2116] font-space text-white shadow-[0_18px_55px_rgba(0,0,0,0.48),0_0_40px_rgba(34,197,94,0.12)]"
      aria-label="Member sale"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_50%,rgba(110,231,183,0.2),transparent_24%),radial-gradient(circle_at_78%_0%,rgba(250,204,21,0.1),transparent_26%),linear-gradient(105deg,rgba(3,18,11,0.98),rgba(15,64,38,0.98)_48%,rgba(5,25,15,0.98))]"
        aria-hidden="true"
      />
      <div className="relative grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 px-3 py-2.5 pr-12 sm:flex sm:min-h-[56px] sm:gap-4 sm:px-5 sm:py-2 sm:pr-14">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-200 text-[#062012] shadow-[0_0_18px_rgba(110,231,183,0.42)]">
          <FiZap className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <div className="min-w-0 sm:flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-emerald-100/75">
            {isPreview && <span>Preview · </span>}
            {strip.title}
          </p>
          <p className="text-[13px] font-semibold sm:hidden">{strip.short}</p>
          <p className="hidden text-sm font-semibold sm:block">{strip.line}</p>
        </div>
        <div className="col-span-2 flex items-center justify-between gap-3 sm:contents">
          <Countdown compact endsAt={endsAt} clockOffset={clockOffset} />
          <button
            type="button"
            onClick={onOpen}
            className="group inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-xl bg-gradient-to-b from-white to-emerald-100 px-3 text-xs font-bold text-[#062012] shadow-[0_8px_28px_rgba(110,231,183,0.28),inset_0_1px_0_white] transition hover:-translate-y-0.5 hover:from-emerald-100 hover:to-emerald-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 motion-reduce:hover:translate-y-0"
          >
            {strip.cta}
            <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss this member sale banner"
        className="absolute right-1 top-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-emerald-100/80 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 sm:top-1/2 sm:-translate-y-1/2"
      >
        <XIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
